/**
 * shell-bridge (Phase 5 item #45) - client-side PostMessage protocol that
 * pairs with useEmbeddedMode.
 *
 * Defines the message schema between POE and a host Shell when POE is
 * iframed. POE acts as a child:
 *   - sends `shell:poe-ready` on mount
 *   - listens for `shell:hello`, `shell:navigate`, `shell:set-tenant`
 *   - posts `poe:event` upward for cross-app events the Shell may surface
 *
 * Standalone behavior: every public function is a no-op (or returns null)
 * when `window.parent === window`. Tasmeer carve-out preserved.
 */

export type ShellInbound =
  | { type: 'shell:hello'; origin: string }
  | { type: 'shell:navigate'; path: string }
  | { type: 'shell:set-tenant'; tenantId: string };

export type ShellOutbound =
  | { type: 'shell:poe-ready' }
  | { type: 'poe:event'; eventType: string; payload?: unknown }
  | { type: 'poe:navigated'; path: string };

const isEmbedded = (): boolean =>
  typeof window !== 'undefined' && window.parent !== window;

/**
 * Post a typed outbound message to the parent shell. No-op when standalone.
 * The parent is identified by origin via the prior `shell:hello` handshake;
 * we use '*' here because POE doesn't pin the origin (the parent can
 * decide whether to trust us).
 */
export function postToShell(msg: ShellOutbound): void {
  if (!isEmbedded()) return;
  try {
    window.parent.postMessage(msg, '*');
  } catch {
    /* parent rejected; nothing to do */
  }
}

/**
 * Subscribe to inbound messages from the shell. Returns an unsubscribe fn.
 * The handler is called only when the message matches the ShellInbound
 * schema; malformed payloads are silently ignored.
 */
export function subscribeToShell(handler: (msg: ShellInbound) => void): () => void {
  if (typeof window === 'undefined') return () => { /* SSR */ };
  const listener = (e: MessageEvent) => {
    const data = e.data as Partial<ShellInbound> | undefined;
    if (!data?.type) return;
    if (
      data.type === 'shell:hello' ||
      data.type === 'shell:navigate' ||
      data.type === 'shell:set-tenant'
    ) {
      handler(data as ShellInbound);
    }
  };
  window.addEventListener('message', listener);
  return () => window.removeEventListener('message', listener);
}

/**
 * Convenience: tell the shell we successfully navigated, so it can update
 * its own URL state if the shell is mirroring routes.
 */
export function notifyShellNavigation(path: string): void {
  postToShell({ type: 'poe:navigated', path });
}

/**
 * Convenience: tell the shell about a cross-app-relevant event (e.g. a deal
 * was created, a task completed). The shell decides whether to surface it.
 */
export function emitShellEvent(eventType: string, payload?: unknown): void {
  postToShell({ type: 'poe:event', eventType, payload });
}
